import DOMPurify from "dompurify";

export const artifactDomPurifyConfig = {
  USE_PROFILES: { html: true, svg: true },
  ADD_ATTR: ["style"],
  FORBID_TAGS: ["script", "iframe", "object", "embed"],
  FORBID_ATTR: ["action", "method"],
};

const legacyTemplateStyles = {
  form: "margin: 0;",
  card: "background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%); border: 1px solid #dde5f3; border-radius: 20px; box-shadow: 0 16px 40px rgba(31, 45, 78, 0.08); color: #172033; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; padding: 24px;",
  header:
    "border-bottom: 1px solid #e8edf6; margin-bottom: 20px; padding-bottom: 18px;",
  kicker:
    "color: #4764ff; font-size: 12px; font-weight: 700; letter-spacing: 0.08em; margin: 0 0 8px; text-transform: uppercase;",
  heading:
    "color: #172033; font-size: 24px; letter-spacing: -0.03em; line-height: 1.2; margin: 0;",
  description:
    "color: #65728a; font-size: 14px; line-height: 1.55; margin: 10px 0 0;",
  grid: "display: grid; gap: 16px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); margin-bottom: 16px;",
  label:
    "color: #344054; display: block; font-size: 13px; font-weight: 650; line-height: 1.4;",
  firstLabel:
    "color: #344054; display: block; font-size: 13px; font-weight: 650; grid-column: 1 / -1; line-height: 1.4;",
  control:
    "border: 1px solid #cfd6e4; border-radius: 10px; box-sizing: border-box; display: block; font: inherit; margin-top: 6px; max-width: 100%; padding: 10px 12px; width: 100%;",
  footer:
    "align-items: center; display: flex; flex-wrap: wrap; gap: 16px; justify-content: space-between; margin-top: 18px;",
  small: "color: #65728a; font-size: 12px; line-height: 1.4;",
  button:
    "background: #172033; border: 0; border-radius: 10px; color: #ffffff; cursor: pointer; font: inherit; font-weight: 650; margin-top: 12px; padding: 11px 16px;",
};

function setStyleIfMissing(element, style) {
  if (!element || element.getAttribute("style")) return;
  element.setAttribute("style", style);
}

function applyLegacyArtifactTemplateStyles(html) {
  const source = html || "";
  if (!source.includes("artifact-template-")) return source;

  const document = new DOMParser().parseFromString(source, "text/html");
  document.querySelectorAll(".artifact-template-form").forEach((element) => {
    setStyleIfMissing(element, legacyTemplateStyles.form);
  });
  document.querySelectorAll(".artifact-template-card").forEach((element) => {
    setStyleIfMissing(element, legacyTemplateStyles.card);
  });
  document.querySelectorAll(".artifact-template-header").forEach((element) => {
    setStyleIfMissing(element, legacyTemplateStyles.header);
    setStyleIfMissing(
      element.querySelector("h2"),
      legacyTemplateStyles.heading
    );
    element
      .querySelectorAll("p:not(.artifact-template-kicker)")
      .forEach((paragraph) =>
        setStyleIfMissing(paragraph, legacyTemplateStyles.description)
      );
  });
  document.querySelectorAll(".artifact-template-kicker").forEach((element) => {
    setStyleIfMissing(element, legacyTemplateStyles.kicker);
  });
  document.querySelectorAll(".artifact-template-grid").forEach((element) => {
    setStyleIfMissing(element, legacyTemplateStyles.grid);
    element.querySelectorAll("label").forEach((label, index) => {
      setStyleIfMissing(
        label,
        index === 0
          ? legacyTemplateStyles.firstLabel
          : legacyTemplateStyles.label
      );
    });
  });
  document
    .querySelectorAll(".artifact-template-form label")
    .forEach((label) => setStyleIfMissing(label, legacyTemplateStyles.label));
  document
    .querySelectorAll(
      ".artifact-template-form input, .artifact-template-form select, .artifact-template-form textarea"
    )
    .forEach((control) =>
      setStyleIfMissing(control, legacyTemplateStyles.control)
    );
  document
    .querySelectorAll(".artifact-template-form button")
    .forEach((button) =>
      setStyleIfMissing(button, legacyTemplateStyles.button)
    );
  document.querySelectorAll(".artifact-template-footer").forEach((element) => {
    setStyleIfMissing(element, legacyTemplateStyles.footer);
    element
      .querySelectorAll("small")
      .forEach((small) => setStyleIfMissing(small, legacyTemplateStyles.small));
  });

  return serializeArtifactDocument(document);
}

function containsUnsafeCss(css = "") {
  const normalized = String(css).toLowerCase().replace(/\s+/g, "");
  return (
    normalized.includes("@import") ||
    normalized.includes("javascript:") ||
    normalized.includes("vbscript:") ||
    normalized.includes("expression(") ||
    normalized.includes("behavior:")
  );
}

function serializeArtifactDocument(document) {
  const headStyles = Array.from(document.head.querySelectorAll("style"))
    .map((element) => element.outerHTML)
    .join("");
  return `${headStyles}${document.body.innerHTML}`;
}

function serializeStyleBlock(css = "") {
  return `<style>${String(css).replace(/<\/style/gi, "<\\/style")}</style>`;
}

function removeUnsafeCss(html) {
  const document = new DOMParser().parseFromString(html || "", "text/html");
  document.querySelectorAll("[style]").forEach((element) => {
    if (containsUnsafeCss(element.getAttribute("style"))) {
      element.removeAttribute("style");
    }
  });
  const safeStyleBlocks = [];
  document.querySelectorAll("style").forEach((element) => {
    if (containsUnsafeCss(element.textContent)) {
      element.remove();
      return;
    }
    safeStyleBlocks.push(serializeStyleBlock(element.textContent));
    element.remove();
  });
  return {
    safeStyleBlocks: safeStyleBlocks.join(""),
    html: document.body.innerHTML,
  };
}

export function sanitizeArtifactHtml(serverSanitizedHtml) {
  const { safeStyleBlocks, html } = removeUnsafeCss(
    applyLegacyArtifactTemplateStyles(serverSanitizedHtml || "")
  );
  return `${safeStyleBlocks}${DOMPurify.sanitize(
    html,
    artifactDomPurifyConfig
  )}`;
}
